// src/utils/navAccess.js
//
// What content an organization has switched on, answered in one place so the
// MENU and the ROUTER can never disagree. Hiding a tab in the sidebar is
// presentation; a client that curates content for minors needs the hidden tab
// to be unreachable — by direct link, by a stale bookmark, by a link from
// somewhere else in the app.
//
// Source of truth is the org's existing `navConfig` (edited in Super Admin →
// Menu Configuration). Each entry is { id, label, isVisible, subItems? }:
//   isVisible === false  -> the whole section is off
//   subItems: [ids]      -> only these sub-topics of the section are on
//   subItems absent      -> every sub-topic is on (the default; legacy configs)
//
// Anything navConfig does not mention is allowed. That keeps every tab that is
// not a content section (dashboard, assessment, pulse, journal, coach and admin
// views) out of this module's reach, and means an unconfigured org behaves
// exactly as before.

import { NAV_TOPIC_GROUPS, parentOfTopic, MINOR_EXCLUDED_TOPIC_IDS } from '../data/navTopics';
import { isCustomId, customSections, customTopicIds } from './customNav';

// Tabs that must never be blocked, whatever a config says. Locking a member
// out of their home screen or their check-in is not a content decision.
const ALWAYS_ALLOWED = new Set(['dashboard', 'assessment', 'pulse']);

// THE YOUTH FLOOR. Under-18s on the athlete surface get the org's own content
// choices minus a platform floor the org can tighten but never loosen:
//   under 18  - no addictions / gambling / eating disorders / trauma topics, and
//               no staff or parent sections (leadership, career, transition,
//               family huddle)
//   under 13  - no Financial Wellbeing; Mental Health off if the org says so
// Every function below takes an optional ageTier so the MENU and the ROUTER
// apply the same floor; without one they behave exactly as before.
const MINOR_EXCLUDED_SECTIONS = ['leadership', 'career', 'transition', 'family-huddle'];
export const isMinorTier = (ageTier) => ageTier === 'kid' || ageTier === 'teen';
export function youthExcludedIds(org, ageTier) {
    if (!isMinorTier(ageTier)) return new Set();
    const out = new Set([...MINOR_EXCLUDED_TOPIC_IDS, ...MINOR_EXCLUDED_SECTIONS]);
    if (ageTier === 'kid') {
        out.add('finance');
        if (org?.youthContent?.hideMentalHealthUnder13) out.add('mental-health');
    }
    return out;
}
// A sidebar menu with the floor applied: sections removed, sub-topics pruned,
// and a topic section dropped once it has no sub-topics left.
export const applyYouthFloor = (items, org, ageTier) => {
    const floor = youthExcludedIds(org, ageTier);
    if (floor.size === 0) return items;
    return items
        .filter((i) => !floor.has(i.id))
        .map((i) => (Array.isArray(i.subItems) ? { ...i, subItems: i.subItems.filter((s) => !floor.has(s.id)) } : i))
        .filter((i) => !NAV_TOPIC_GROUPS[i.id] || (i.subItems && i.subItems.length > 0));
};

const entryFor = (org, id) =>
    (Array.isArray(org?.navConfig) ? org.navConfig.find((i) => i.id === id) : null) || null;

export const isSectionVisible = (org, sectionId) => {
    const entry = entryFor(org, sectionId);
    return !entry || entry.isVisible !== false;
};

// The sub-topics of a section this org allows, in the platform's order.
export function allowedSubTopics(org, sectionId, ageTier = null) {
    const all = NAV_TOPIC_GROUPS[sectionId] || [];
    if (!isSectionVisible(org, sectionId)) return [];
    const floor = youthExcludedIds(org, ageTier);
    if (floor.has(sectionId)) return [];
    const entry = entryFor(org, sectionId);
    if (!entry || !Array.isArray(entry.subItems)) return all.filter((t) => !floor.has(t.id));
    const allow = new Set(entry.subItems);
    return all.filter((t) => allow.has(t.id) && !floor.has(t.id));
}

export const allowedSubTopicIds = (org, sectionId, ageTier = null) => allowedSubTopics(org, sectionId, ageTier).map((t) => t.id);

// May this tab be opened under this org's configuration?
export function isTabAllowed(org, tabId, ageTier = null) {
    if (!tabId || ALWAYS_ALLOWED.has(tabId)) return true;
    if (youthExcludedIds(org, ageTier).has(tabId)) return false;

    // An org-defined section or topic (utils/customNav.js) exists only while the
    // org still lists it. A removed section must not stay reachable by an old
    // link, and one org's custom id means nothing under another org.
    if (isCustomId(tabId)) {
        return customTopicIds(org).includes(tabId) || customSections(org).some((s) => s.id === tabId);
    }

    // A sub-topic: its section must be on, and it must be in the section's list.
    const parent = parentOfTopic(tabId);
    if (parent) return allowedSubTopicIds(org, parent, ageTier).includes(tabId);

    // A section with sub-topics is only useful if at least one survives.
    if (NAV_TOPIC_GROUPS[tabId]) return allowedSubTopicIds(org, tabId, ageTier).length > 0;

    // Any other tab: allowed unless the org explicitly hid it.
    return isSectionVisible(org, tabId);
}

// Where a section tab should land: its first allowed sub-topic. The old
// behaviour hardcoded 'anxiety' / 'resilience', which an allowlist can remove.
export const firstAllowedTopic = (org, sectionId, ageTier = null) => allowedSubTopicIds(org, sectionId, ageTier)[0] || null;
